import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError, api } from '@/lib/api';

/**
 * Публичная страница оплаты по ссылке /pay/{token}.
 * Не требует авторизации. Видна родителю, которому отправили ссылку.
 *
 * Этапы:
 *   1) Грузим данные договора через GET /api/public/pay/{token}.
 *      Видим ученика, договор, ежемесячную сумму, полную сумму, оплачено
 *      и остаток. Если уже есть pending-платёж — показываем «Продолжить
 *      оплату» с прежней суммой.
 *   2) Поле «Сумма» по умолчанию = ежемесячная (но не больше остатка).
 *      Не даём ввести больше остатка.
 *   3) Кнопка «Оплатить» → POST /api/public/pay/{token}/charge {amount}.
 *      Бэк отменяет старые pending в ЮКассе и создаёт новый платёж,
 *      возвращает confirmation_url — редиректим туда (для СБП в ЮКассе
 *      это страница с QR/диплинком).
 */

function fmtMoney(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₽';
}

export default function PublicPayPage() {
  const { token } = useParams();

  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(true);

  const [amount, setAmount]   = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr]   = useState(null);

  const reload = () => {
    setError(null);
    setLoading(true);
    api.get(`/api/public/pay/${encodeURIComponent(token || '')}`)
      .then((res) => {
        setData(res);
        // Дефолт — ежемесячная сумма, но не больше остатка.
        const monthly = Number(res?.summary?.monthly) || 0;
        const remain  = Number(res?.summary?.remaining) || 0;
        if (monthly > 0) setAmount(String(Math.min(monthly, remain).toFixed(2)).replace(/\.00$/, ''));
        else if (remain > 0) setAmount(String(remain.toFixed(2)).replace(/\.00$/, ''));
      })
      .catch((e) => {
        if (e instanceof ApiError) setError(e.message);
        else setError('Не удалось загрузить договор');
      })
      .finally(() => setLoading(false));
  };
  useEffect(reload, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const value = parseFloat(String(amount).replace(',', '.'));
    if (!isFinite(value) || value <= 0) {
      setSubmitErr('Введите сумму больше нуля');
      return;
    }
    const remain = Number(data?.summary?.remaining) || 0;
    if (value - remain > 0.005) {
      setSubmitErr('Сумма больше остатка к оплате');
      return;
    }
    setSubmitErr(null);
    setSubmitting(true);
    try {
      const res = await api.post(`/api/public/pay/${encodeURIComponent(token)}/charge`, { amount: value });
      // ЮКасса для bank_card возвращает confirmation_url (redirect),
      // для СБП — confirmation_data (диплинк/QR). У нас включён только
      // СБП — но обе ветки обрабатываем на всякий случай.
      const url  = res?.confirmationUrl;
      const data = res?.confirmationData;
      if (url) {
        window.location.href = url;
        return;
      }
      if (data) {
        // СБП-диплинк (mobile-приложение банка). На десктопе раскроется
        // QR-страница в новой вкладке тоже через confirmationUrl, но если
        // её нет — попробуем сам diplink.
        window.location.href = String(data);
        return;
      }
      setSubmitErr('Платёж создан, но ЮКасса не вернула ссылку. Обратитесь в школу.');
    } catch (err) {
      setSubmitErr(err instanceof ApiError ? err.message : 'Не удалось создать платёж');
    } finally {
      setSubmitting(false);
    }
  };

  const onContinuePending = () => {
    const url = data?.pendingPayment?.confirmationUrl;
    if (url) window.location.href = url;
  };

  return (
    <div className="pp-page">
      <div className="pp-card">
        <div className="pp-logo">
          <img src="/img/np-logo.png" alt="Новое Поколение" />
        </div>

        {loading && <div className="pp-empty">Загрузка…</div>}

        {error && (
          <div className="pp-error" role="alert">
            <b>Ссылка недействительна.</b>
            <div style={{ marginTop: 4, fontSize: 13, color: '#666' }}>
              Возможно, договор расторгнут или ссылка устарела. Обратитесь в школу.
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>{error}</div>
          </div>
        )}

        {!loading && data && (
          <>
            <PayInfo data={data} />

            {data.pendingPayment && (
              <div className="pp-pending">
                <div>
                  По этому договору уже создан платёж на <b>{fmtMoney(data.pendingPayment.amount)}</b>.
                  Можно продолжить его оплату или создать новый.
                </div>
                <button type="button" className="pp-btn pp-btn--primary" onClick={onContinuePending}>
                  Продолжить начатый платёж
                </button>
              </div>
            )}

            <form onSubmit={onSubmit} className="pp-form">
              <div className="pp-amount-row">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  max={Number(data.summary.remaining) || undefined}
                  value={amount}
                  onChange={(e) => { setAmount(e.target.value); if (submitErr) setSubmitErr(null); }}
                  className="pp-amount"
                  disabled={submitting || Number(data.summary.remaining) <= 0}
                />
                <button
                  type="submit"
                  className="pp-btn pp-btn--primary pp-btn--lg"
                  disabled={submitting || Number(data.summary.remaining) <= 0}
                >
                  {submitting ? 'Создаём платёж…' : 'Оплатить'}
                </button>
              </div>
              {submitErr && <div className="pp-error pp-error--inline">{submitErr}</div>}
              {Number(data.summary.remaining) <= 0 && (
                <div className="pp-hint">Договор полностью оплачен. Спасибо!</div>
              )}
              <div className="pp-hint">
                Оплата через ЮКассу (СБП). После нажатия откроется страница банка.
              </div>
            </form>
          </>
        )}
      </div>

      <OrgFooter />

      <style>{`
        .pp-page {
          min-height: 100vh;
          background: #f3f4f6;
          display: flex; flex-direction: column; align-items: center;
          padding: 24px 16px;
        }
        .pp-logo { display: flex; justify-content: center; margin-bottom: 16px; }
        /* 72px — близко к исходному размеру растра, без апскейла и без
           заметного размытия. Высота больше — пикселизация лезет наружу. */
        .pp-logo img { height: 72px; width: auto; }
        .pp-card {
          width: 100%; max-width: 560px;
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 6px 24px rgba(0,0,0,0.06);
          padding: 20px;
          margin-bottom: 16px;
        }
        .pp-empty { padding: 32px; text-align: center; color: #666; }
        .pp-info { border-top: 1px solid #eee; }
        .pp-row {
          display: flex; justify-content: space-between; align-items: baseline;
          padding: 10px 4px;
          border-bottom: 1px solid #eee;
          font-size: 14px;
          gap: 12px;
        }
        .pp-row__label { color: #555; }
        .pp-row__value { font-weight: 500; color: #111; }
        .pp-row__value--big { font-size: 16px; }
        .pp-row__value--remaining { color: #b45309; font-weight: 600; }
        .pp-pending {
          margin-top: 12px;
          padding: 10px 12px;
          border-radius: 8px;
          background: #fff7ed; color: #7c2d12;
          font-size: 13.5px;
          display: flex; flex-direction: column; gap: 8px;
        }
        .pp-form { margin-top: 16px; }
        .pp-amount-row { display: flex; gap: 8px; align-items: stretch; }
        .pp-amount {
          flex: 1; min-width: 0;
          padding: 12px 14px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 18px;
          background: #fff;
        }
        .pp-btn {
          padding: 10px 18px;
          border: 0; border-radius: 8px;
          font-size: 14px; font-weight: 500;
          cursor: pointer;
        }
        .pp-btn--primary { background: #2563eb; color: #fff; }
        .pp-btn--primary:disabled { background: #93b4f4; cursor: not-allowed; }
        .pp-btn--lg { padding: 12px 24px; font-size: 16px; }
        .pp-error {
          margin-top: 12px;
          padding: 10px 12px;
          background: #fef2f2; color: #991b1b;
          border-radius: 8px;
          font-size: 14px;
        }
        .pp-error--inline { margin-top: 8px; padding: 8px 10px; }
        .pp-hint { margin-top: 8px; color: #666; font-size: 12.5px; text-align: center; }

        .pp-foot {
          max-width: 720px; margin-top: 8px;
          font-size: 11.5px; color: #6b7280; line-height: 1.5;
          text-align: center;
        }
        .pp-foot a { color: #4b5563; text-decoration: none; }
      `}</style>
    </div>
  );
}

function PayInfo({ data }) {
  const s = data.summary || {};
  return (
    <div className="pp-info">
      <div className="pp-row">
        <span className="pp-row__label">Ученик:</span>
        <span className="pp-row__value">{data.student?.name || '—'}</span>
      </div>
      <div className="pp-row">
        <span className="pp-row__label">Договор:</span>
        <span className="pp-row__value">
          № {data.agreement?.number}
          {data.agreement?.date && <> от {data.agreement.date.split('-').reverse().join('.')}</>}
        </span>
      </div>
      <div className="pp-row">
        <span className="pp-row__label">Ежемесячная оплата:</span>
        <span className="pp-row__value pp-row__value--big">{fmtMoney(s.monthly)}</span>
      </div>
      <div className="pp-row">
        <span className="pp-row__label">Полная сумма договора:</span>
        <span className="pp-row__value pp-row__value--big">{fmtMoney(s.total)}</span>
      </div>
      <div className="pp-row">
        <span className="pp-row__label">Оплачено всего:</span>
        <span className="pp-row__value pp-row__value--big">{fmtMoney(s.paid)}</span>
      </div>
      <div className="pp-row">
        <span className="pp-row__label">Остаток к оплате:</span>
        <span className="pp-row__value pp-row__value--big pp-row__value--remaining">
          {fmtMoney(s.remaining)}
        </span>
      </div>
    </div>
  );
}

function OrgFooter() {
  return (
    <footer className="pp-foot">
      © АНО ЧОШ «НОВОЕ ПОКОЛЕНИЕ» · ИНН 5032339370 · КПП 503201001 · ОГРН 1225000030243<br />
      <a
        href="https://islod.obrnadzor.gov.ru/view/186112"
        target="_blank"
        rel="noopener noreferrer"
      >
        Лицензия № ЛО35‑01255‑50/00891601 от 23.11.2023
      </a><br />
      <a
        href="https://isga.obrnadzor.gov.ru/accredreestr/details/57ee0edf-b1cb-b07e-3124-d2e4b815b58e"
        target="_blank"
        rel="noopener noreferrer"
      >
        Гос. аккредитация № А007‑01255‑50/01309224 от 23.07.2024
      </a>
    </footer>
  );
}
